import React, {useMemo} from "react"
import {extractAvailableSeasons, generateCountByKey, getRegistryObject, getSeason, getSeasonLabel} from "../utils";
import HighchartsReact from "highcharts-react-official";
import {useTranslation} from "react-i18next";
import useHighChartLocaleOptions from "../useHighChartLocaleOptions";
import Highcharts, {chartLogoOptions, creditsOptions, exportingOptions} from "../highChartsBase";
import Toolbar, {useToolbar} from "./Toolbar";


const aspectCodes = [
    "2", // N
    "3", // NE
    "4", // E
    "5", // SE
    "1", // S
    "6", // SO
    "7", // O
    "8"  // NO
]

const getAspectCategory = (aspect) => {
    // Category: COLD if aspect is NO N NE E
    // Category: WARM if aspect is SO S SE O
    if (["2", "3", "4", "8"].includes(aspect)) {
        return "COLD";
    } else if (["1", "5", "6", "7"].includes(aspect)) {
        return "WARM";
    } else {
        return null;
    }
}

const IncidentAspectPolar = ({data: originalData, registry={}}) => {
    const {t} = useTranslation("graphs");
    const highChartLocaleOptions = useHighChartLocaleOptions();
    const aspectRegistry = getRegistryObject(registry, 'aspect');

    const height = 500;
    const attribute = 'aspect'

    const data = useMemo(() => (originalData.map(({date, ...rest}) => {
            return {
                date,
                season: getSeason(date),
                aspectCategory: getAspectCategory(rest.aspect),
                ...rest
            }
        })
    ), [originalData]);

    const availableSeasons = useMemo( () => extractAvailableSeasons(data), [data] );
    const toolbar = useToolbar({availableSeasons});

    // Get the data filtered by the toolbar selections
    const filteredData = useMemo( () => (
        data.filter( item => {
            const itemSeason = item.season;
            return (toolbar.seasonFrom ? itemSeason >= toolbar.seasonFrom : true)  && (toolbar.seasonTo ? itemSeason <= toolbar.seasonTo : true)
        })
    ), [data, toolbar.seasonFrom, toolbar.seasonTo]);

    const aspectStat = generateCountByKey(filteredData, attribute);

    const categoryLabels = aspectCodes.map(code => aspectRegistry?.[code] ?? code);
    const graphData = aspectCodes.map(k => aspectStat[k] ?? 0)

    const totalAspectCold = useMemo(
        () => filteredData.filter(item => getAspectCategory(item.aspect) === "COLD").length,
        [filteredData]
    );
    const totalAspectWarm = useMemo(
        () => filteredData.filter(item => getAspectCategory(item.aspect) === "WARM").length,
        [filteredData]
    );

    const subtitle = t('subtitle.seasonFrom', {seasonFrom: getSeasonLabel(toolbar.seasonFrom)}) + (toolbar.seasonTo ? t('subtitle.seasonTo', {seasonTo: getSeasonLabel(toolbar.seasonTo)}) : '');

    const options = {
        ...highChartLocaleOptions,
        exporting: {
            ...exportingOptions.exporting,
            filename: `incident_by_aspect`
        },
        title: {
            text: t('incidentAspectPolar.title')
        },
        subtitle: {
            text: subtitle
        },
        chart: {
            polar: true,
            type: 'column',
            height: height,
            ...chartLogoOptions,
        },
        ...creditsOptions,
        pane: {
            size: '85%'
        },

        xAxis: {
            categories: categoryLabels,
            tickmarkPlacement: 'on',
            lineWidth: 0,
        },

        yAxis: {
            min: 0,
            endOnTick: false,
            showLastLabel: true,
            title: {
                text: null
            }
        },

        legend: {
            enabled: false
        },

        series: [
            {
                data: graphData,
                name: t(`incidentAspectPolar.totalIncidents`),
                pointPlacement: 'on',
                type: 'area'
            }
        ],
        caption: {
            align: "center",
            text: t('incidentAspectPolar.caption', {coldIncidents: totalAspectCold, warmIncidents: totalAspectWarm})
        },
    }

    return <>
        <Toolbar {...toolbar} />
        <HighchartsReact highcharts={Highcharts} options={options} />
    </>
}

export default IncidentAspectPolar